'use client'

import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'

/**
 * Customer.io CDP
 *
 * This module owns loading the Customer.io browser SDK and exposing a singleton accessor.
 */

const CUSTOMER_IO_IN_APP_PLUGIN = 'Customer.io In-App Plugin'

export const CUSTOMER_IO_IN_APP_EVENT = 'customerio:in-app-message'

let customerIoAnalyticsInstance: ReturnType<
  typeof AnalyticsBrowser.load
> | null = null

function isTruthyEnv(value: string | undefined) {
  return /^(1|true|yes)$/i.test(value?.trim() ?? '')
}

function getInAppEventPayload(event: Event) {
  const detail =
    event instanceof CustomEvent && typeof event.detail === 'object'
      ? event.detail
      : {}

  return {
    ...(detail as Record<string, unknown>),
    type: event.type,
  }
}

function handleInAppEvent(event: Event) {
  const detail = getInAppEventPayload(event)

  if (process.env.NEXT_PUBLIC_TRACKING_DEBUG === '1') {
    console.debug('[customerio.in-app]', detail)
  }

  window.dispatchEvent(
    new CustomEvent(CUSTOMER_IO_IN_APP_EVENT, {
      detail,
    })
  )
}

function getCustomerIoLoadOptions(): Parameters<typeof AnalyticsBrowser.load>[1] {
  const siteId = process.env.NEXT_PUBLIC_CUSTOMER_IO_SITE_ID?.trim()

  if (!siteId) {
    return undefined
  }

  const loadOptions = {
    integrations: {
      [CUSTOMER_IO_IN_APP_PLUGIN]: {
        siteId,
        anonymousInApp: isTruthyEnv(
          process.env.NEXT_PUBLIC_CUSTOMER_IO_ANONYMOUS_IN_APP
        ),
        events: handleInAppEvent,
      },
    },
  }

  // Customer.io documents `events` as a function, but the package's
  // InitOptions type currently narrows integration options to JSON values.
  return loadOptions as unknown as Parameters<typeof AnalyticsBrowser.load>[1]
}

/**
 * Get Customer.io analytics instance.
 * Only initializes when accessed and if write key is available.
 */
export const getCustomerIoAnalytics = () => {
  if (typeof window === 'undefined') {
    return null
  }

  // Return existing instance if already initialized
  if (customerIoAnalyticsInstance !== null) {
    return customerIoAnalyticsInstance
  }

  const writeKey = process.env.NEXT_PUBLIC_CUSTOMER_IO_WRITE_KEY?.trim()
  const cdnURL = process.env.NEXT_PUBLIC_CUSTOMER_IO_CDN_URL?.trim()

  // Only initialize if write key exists and is not empty
  if (!writeKey) {
    return null
  }

  try {
    const analyticsSettings: Parameters<typeof AnalyticsBrowser.load>[0] = {
      writeKey,
    }

    if (cdnURL) {
      analyticsSettings.cdnURL = cdnURL
    }

    customerIoAnalyticsInstance = AnalyticsBrowser.load(
      analyticsSettings,
      getCustomerIoLoadOptions()
    )
    return customerIoAnalyticsInstance
  } catch (error) {
    // Silently fail if initialization fails
    console.warn('Failed to initialize Customer.io analytics:', error)
    return null
  }
}

export const getCustomerIoInbox = (...topics: string[]) => {
  const analytics = getCustomerIoAnalytics()
  if (!analytics?.inbox) return null

  try {
    return analytics.inbox(...topics)
  } catch {
    return null
  }
}

/**
 * Legacy exports (kept for backwards compatibility).
 * Prefer `getCustomerIoAnalytics` and `CustomerIoCdp`.
 */
export const CustomerIoCdp = () => null
